import EdFormControl from '@Components/UI/Inputs/EdFormControl/EdFormControl';
import { UnsavedChangesGuard } from '@RichTextEditor';
import RichTextEditor from '@Components/UI/RichTextEditor/RichTextEditor';
import { FlexLayout } from '@eduact/ed-system';
import { yupResolver } from '@hookform/resolvers/yup';
import { Checkbox, Icon, MenuItem, MenuList, Paper } from '@mui/material';
import { ClickAwayListener, Grow } from '@mui/material';
import { Button, ButtonGroup, InputAdornment, Popper, TextField } from '@mui/material';
import { Spacer } from '@Styled/utilities';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { ContentConfig } from '../../../../Constants/CourseContent.constants';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { ASSIGNMENT_SCHEMA } from '../../Schema/Assignment.schema';
import { Assignments } from '../../Types/Assignment.types';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import EdUploadFile from '@Components/UI/Inputs/EdUploadFile/EdUploadFile';
import AppRegistrationTwoToneIcon from '@mui/icons-material/AppRegistrationTwoTone';
import DeleteIcon from '@mui/icons-material/Delete';
import { uploadDocument } from '@Services/uploads/uploads.req';
import { useSnackbar } from '@Providers/useSnackbar';
import { AssignmentRequester } from '../../Services/Assignment.requester';
import { useTypedSelector } from '@Features/store';
import { DevTool } from '@hookform/devtools';
import EdConfirmModal from '@Components/UI/Modals/EdConfirmModal/EdConfirmModal';
import { UnitTypeId } from '@Constants/unitTypes';
import { useUnitPermissions } from '@Pages/ClassRooms/Pages/SingleCourse/Pages/CourseForm/Pages/CourseContent/useUnitPermissions';

const AddAssignment = () => {
	const editorRef = useRef<any>(null);
	const { displaySnackbar } = useSnackbar();
	const [toggle, setToggle] = useState<boolean>(false);
	const [type, setType] = useState<string>('attachment');
	const history = useHistory();
	const { currentCourse } = useTypedSelector((state) => state.classroom);
	const { canCreate } = useUnitPermissions();

	useEffect(() => {
		if (!canCreate) history.goBack();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	const [openModel, setOpenModel] = useState<boolean>(false);
	const [switchModel, setSwitchModel] = useState<boolean>(false);
	const handleModelOpen = () => {
		setOpenModel(true);
	};
	const handleModelClose = (close: boolean, confirm?: boolean) => {
		try {
			if (confirm) {
				handleResetFile();
			}
			setOpenModel(false);
		} catch (e) {
			const msg = e.response?.data.message || "Couldn't delete admin";
			displaySnackbar('error', msg);
		}
	};
	const handleSwitchModelOpen = () => {
		setSwitchModel(true);
	};
	const handleSwitchModelClose = (close: boolean, confirm?: boolean) => {
		try {
			if (confirm) {
				setToggle(!toggle);
			}
			setSwitchModel(false);
		} catch (e) {
			const msg = e.response?.data.message || "Couldn't delete admin";
			displaySnackbar('error', msg);
		}
	};

	const { control, formState, handleSubmit, watch, setValue, reset, resetField } =
		useForm<Assignments>({
			mode: 'all',
			resolver: yupResolver(ASSIGNMENT_SCHEMA),
			defaultValues: {
				unit: {
					type_id: UnitTypeId.assignment,
				},
			},
		});
	const watchQues = watch('assignment.question');
	const watchStatus = watch('assignment.show_status');
	const [open, setOpen] = React.useState(false);
	const anchorRef = React.useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (watchStatus === undefined) {
			setValue('assignment.show_status', false);
		}
	}, [watchStatus]);

	const handleToggle = () => {
		setOpen((prevOpen) => !prevOpen);
	};

	const question = useMemo(() => {
		return watchQues?.toString();
	}, [watchQues]);

	const handleClose = (event: Event) => {
		if (anchorRef.current?.contains(event.target as HTMLElement)) {
			return;
		}
		setOpen(false);
	};

	const UploadDocument = async (blob: File | Blob | null) => {
		try {
			const {
				data: {
					data: { uri, extension },
				},
				status,
			} = await uploadDocument({
				document: blob,
				folder: 'documents',
			});

			setValue('assignment.question', uri, {
				shouldValidate: true,
				shouldDirty: true,
				shouldTouch: true,
			});
			setValue('assignment.extension', extension);
			return status === 200;
		} catch (e) {
			const msg = e.response?.data.message || 'Unable to Upload File ';
			displaySnackbar('error', msg);
		}
	};

	const onSave = async (form: Assignments) => {
		try {
			const {
				data: { data },
			} = await AssignmentRequester.getInstance().create({
				...form,
				unit: {
					type_id: UnitTypeId.assignment,
					course_id: currentCourse?.id,
					name: form.unit.name,
				},
				assignment: {
					question: question,
					duration: form.assignment.duration ?? null,
					overall_score: form.assignment.overall_score,
					question_type: type,
					show_status: form.assignment.show_status,
					answer_schema: form.assignment.answer_schema ?? "*",
				},
			});
			history.goBack();
			displaySnackbar('success', `Assignment created successfully`);
		} catch (e) {
			const msg = e?.response?.data?.message || 'Unable to Create Assignment ';
			displaySnackbar('error', msg);
		}
	};

	const handleReset = () => {
		reset({
			unit: {
				type_id: UnitTypeId.assignment,
				name: '',
			},
			assignment: {
				duration: undefined as any,
				overall_score: undefined as any,
				question: '',
				question_type: '',
				show_status: false,
			},
		});
	};

	const onSaveAddAnother = async (form: Assignments) => {
		try {
			const {
				data: { data },
			} = await AssignmentRequester.getInstance().create({
				...form,
				unit: {
					type_id: UnitTypeId.assignment,
					course_id: currentCourse?.id,
					name: form.unit.name,
				},
				assignment: {
					question,
					duration: form.assignment.duration ?? null,
					overall_score: form.assignment.overall_score,
					question_type: type,
					show_status: form.assignment.show_status,
					answer_schema: form.assignment.answer_schema ?? "*",
				},
			});
			displaySnackbar('success', 'Assignment created successfully');
			handleReset();
		} catch (e) {
			const msg = e.response?.data.message || 'Unable to Create Assignment ';
			displaySnackbar('error', msg);
		}
	};

	const handleResetFile = () => {
		try {
			resetField('assignment.question', { keepError: false });
		} catch (error) {
		
		}
	};

	const returnNote = () => {
		return (
			<>
				<p>Are you sure to switch question type ?</p>
				<span>NB: question will be erased</span>
			</>
		);
	};

	if (!canCreate) return null;

	return (
		<>
			<EdConfirmModal
				text="Are you sure you want to delete this File"
				open={openModel}
				handleClose={handleModelClose}
			/>
			<EdConfirmModal
				text={returnNote() as any}
				open={switchModel}
				handleClose={handleSwitchModelClose}
			/>
			<FlexLayout flexDirection="column" px="2.625rem" flex="1">
				<UnsavedChangesGuard
					formState={formState}
				/>
				<EdFormControl
					size="small"
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								{ContentConfig['assignment'].icon}
							</InputAdornment>
						),
					}}
					control={control}
					label="Unit Name"
					name="unit.name"
				/>
				<Spacer mb="1.625rem" />
				<FlexLayout
					flexDirection="column"
					gridGap="0.5rem"
					alignItems="flex-end"
					mt="3rem"
					mb="3.063rem"
				>
					{toggle ? (
						<FlexLayout
							flexDirection="column"
							alignItems="flex-end"
							gridGap="0.5rem"
							width="100%"
						>
							<FlexLayout
								alignItems="end"
								gridGap="0.688rem"
								onClick={() => {
									handleSwitchModelOpen();
									setType('attachment');
									resetField('assignment.question', { keepError: false });
								}}
							>
								<Switch>Switch to Attachment</Switch>
								<AttachFile fontSize="small" />
							</FlexLayout>
							<FlexLayout flex="1" width="100%">
								<Controller
									control={control}
									name="assignment.question"
									render={({ field: { value, onChange, ...props } }) => {
										return (
											<RichTextEditor preset="full"
												{...props}
												value={value as any}
												onChange={onChange}
											/>
										);
									}}
								/>
							</FlexLayout>
						</FlexLayout>
					) : (
						<FlexLayout justifyContent="space-between" width="100%">
							<FlexLayout gridGap="1.313rem" alignItems="center">
								<EdUploadFile
									aspectRation={1}
									folder="documents"
									uploadLabel="UPLOAD FILE"
									uploadIcon={<Icon>upload_file</Icon>}
									control={control}
									name="assignment.question"
									label=""
									type="document"
									onFileUpload={UploadDocument as any}
								/>
								<DeleteIconStyle
									fontSize="small"
									color="disabled"
									onClick={() => handleModelOpen()}
								/>
							</FlexLayout>
							<FlexLayout
								alignItems="center"
								gridGap="0.688rem"
								onClick={() => {
									handleSwitchModelOpen();
									setType('text');
									resetField('assignment.question', { keepError: false });
								}}
							>
								<Switch>Switch to text</Switch>
								<AppRegistrationTwoToneIcon fontSize="small" />
							</FlexLayout>
						</FlexLayout>
					)}
				</FlexLayout>

				<FlexLayout flexDirection="column" gridGap="3.313rem">
					<FlexLayout alignItems="center" gridGap="0.938rem">
						<EdFormControl
							control={control}
							width="4.794rem"
							type="number"
							inputMode="numeric"
							name={`assignment.overall_score`}
							label="Score"
							required
						/>
						<p>Points</p>
					</FlexLayout>
					<FlexLayout alignItems="center" gridGap="0.938rem">
						<EdFormControl
							control={control}
							width="4.794rem"
							type="number"
							inputMode="numeric"
							name={`assignment.duration`}
							label="Duration (blank = no deadline)"
						/>
						<p>Days</p>
					</FlexLayout>

					<FlexLayout
						display="flex"
						alignItems="center"
						gridGap="0.938rem"
						mt="0.625rem"
					>
						<Controller
							name="assignment.show_status"
							control={control}
							render={({ field }) => (
								<FlexLayout
									display="flex"
									alignItems="center"
									gridGap="0.938rem"
									mt="0.625rem"
								>
									<StatusBox
										checked={watchStatus ? true : false}
										{...field}
										id="Status"
										defaultChecked={false}
									/>
									<LabelStyle>Show status (passed or failed)</LabelStyle>
								</FlexLayout>
							)}
						/>
					</FlexLayout>
					<FlexLayout alignItems="center" gridGap="0.938rem">
						<Controller
							name="assignment.answer_schema"
							control={control}
							render={({ field }) => (
								<TextField
									select
									size="small"
									label="Allowed answer type"
									sx={{ minWidth: '14rem' }}
									value={field.value ?? '*'}
									onChange={(e) => field.onChange(e.target.value)}
								>
									<MenuItem value="*">Text or attachment</MenuItem>
									<MenuItem value="text">Text only</MenuItem>
									<MenuItem value="attachment">Attachment only</MenuItem>
								</TextField>
							)}
						/>
					</FlexLayout>
				</FlexLayout>

				<FlexLayout mt="2.688rem" mb="5.688rem" justifyContent="flex-end">
					<Button
						variant="contained"
						color="warning"
						onClick={() => history.goBack()}
					>
						Cancel
					</Button>
					<Spacer mx="0.5rem" />
					<ButtonGroup
						variant="contained"
						ref={anchorRef}
						aria-label="split button"
					>
						<Button
							onClick={handleSubmit(onSave)}
							disabled={
								!formState.isDirty ||
								!formState.isValid ||
								formState.isSubmitting
							}
							variant="contained"
						>
							Save
						</Button>
						<DropButton
							size="small"
							aria-controls={open ? 'split-button-menu' : undefined}
							aria-expanded={open ? 'true' : undefined}
							onClick={handleToggle}
							variant="contained"
							disabled={!formState.isDirty || !formState.isValid}
						>
							<ArrowDropDownIcon />
						</DropButton>
					</ButtonGroup>
					<Popper
						sx={{
							zIndex: 1,
						}}
						open={open}
						anchorEl={anchorRef.current}
						role={undefined}
						transition
						disablePortal
					>
						{({ TransitionProps, placement }) => (
							<Grow
								{...TransitionProps}
								style={{
									transformOrigin:
										placement === 'bottom' ? 'center top' : 'center bottom',
								}}
							>
								<Paper>
									<ClickAwayListener onClickAway={handleClose}>
										<MenuList id="split-button-menu" autoFocusItem>
											<MenuItem
												key={1}
												disabled={formState.isSubmitting}
												onClick={handleSubmit(onSaveAddAnother)}
											>
												Save and add another
											</MenuItem>
										</MenuList>
									</ClickAwayListener>
								</Paper>
							</Grow>
						)}
					</Popper>
				</FlexLayout>
			</FlexLayout>
		</>
	);
};

export const DropButton = styled(Button)`
	min-width: 30px !important;
	padding: 0;
`;
const EdFormControlStyle = styled(EdFormControl)`
	input {
		width: 18px;
		height: 17px;
	}
`;
const AttachFile = styled(AttachFileIcon)`
	transform: rotate(-45deg);
`;
const Switch = styled.p`
	font-size: 1rem;
	font-weight: 500;
	color: #2f2e41;
	text-decoration: underline;
	cursor: pointer;
`;
const LabelStyle = styled.label`
	font-size: 0.9rem;
	color: ${(props) => props.theme.palette.cadet.main};
	font-weight: normal;
	margin-top: 2px;
`;
const DeleteIconStyle = styled(DeleteIcon)`
	cursor: pointer;
`;
const StatusBox = styled(Checkbox)`
	padding: 0;
	svg,
	input {
		width: 20px;
		height: 20px;
	}
`;
export default AddAssignment;
